import * as React from 'react';
import { NumericFormat } from 'react-number-format';
import TextField from '@mui/material/TextField';
import { Box, useMediaQuery } from '@mui/material';


export default function FormattedInputs({
  label = 'Moneda',
  icon: Icon,
  name,
  maxLength,
  required,
  onChange,
  newValue,
  sx,
  matchTextBox = false,
  width,
  allowNegative = false,
  // NUEVOS OPCIONALES (retrocompatibles)
  maxValue,            // tope superior
  minValue,            // piso
  enforce = 'soft',    // 'soft' | 'hard'
  onBlurClamp = true,
  prefix = 'Q',
}) {
  const isMobile = useMediaQuery('(max-width: 768px)');

  // límites calculados (retrocompatibles)
  const min = React.useMemo(() => {
    const base = allowNegative ? (Number.isFinite(minValue) ? Number(minValue) : -Infinity) : 0;
    return Number.isFinite(base) ? base : 0;
  }, [allowNegative, minValue]);

  const cap = React.useMemo(() => {
    const mv = Number(maxValue);
    return Number.isFinite(mv) ? mv : Infinity;
  }, [maxValue]);

  // estado visual (se mantiene el formato previo con string)
  const [value, setValue] = React.useState(
    typeof newValue === 'number' && !isNaN(newValue)
      ? `${prefix}${Number(newValue).toLocaleString()}`
      : ''
  );

  React.useEffect(() => {
    if (typeof newValue === 'number' && !isNaN(newValue)) {
      setValue(`${prefix}${Number(newValue).toLocaleString()}`);
    } else {
      setValue('');
    }
  }, [newValue, prefix]);

  const clamp = React.useCallback((n) => {
    if (n == null || isNaN(n)) return 0;
    let x = n;
    if (!allowNegative) x = Math.max(min, x);
    else if (Number.isFinite(min)) x = Math.max(min, x);
    if (Number.isFinite(cap)) x = Math.min(cap, x);
    // redondeo 2 decimales
    return Math.round(x * 100) / 100;
  }, [min, cap, allowNegative]);

  const handleChange = (values) => {
    const { value: rawValue, formattedValue, floatValue } = values;
    // Formato visual siempre sigue el tecleo
    setValue(formattedValue);

    // Envío hacia arriba (clamp en 'soft' también, pero solo afecta si pasan maxValue)
    const numericValue = typeof floatValue === 'number' ? floatValue : parseFloat(String(rawValue || '').replace(/,/g, ''));
    const outgoing = clamp(numericValue);
    onChange?.(name, isNaN(outgoing) ? 0 : outgoing);
  };

  const handleBlur = () => {
    if (!onBlurClamp) return;
    // Cuando salimos del campo, forzamos que lo visual refleje el clamp
    const stripped = String(value || '').replace(/[^0-9.\-]/g, '');
    const n = parseFloat(stripped);
    const fixed = clamp(n);
    setValue(fixed === 0 && !required ? '' : `${prefix}${Number(fixed).toLocaleString()}`);
    onChange?.(name, isNaN(fixed) ? 0 : fixed);
  };

  // isAllowed para modo 'hard' (bloqueo en tiempo real)
  const isAllowed = (vals) => {
    if (enforce !== 'hard') return true;
    const { floatValue } = vals;
    if (floatValue == null) return true; // permitir vacío
    if (floatValue < (Number.isFinite(min) ? min : -Infinity)) return false;
    if (Number.isFinite(cap) && floatValue > cap) return false;
    return true;
  };

  // ancho opcional para igualar TextBox sin romper usos existentes
  const computedWidth = matchTextBox ? (isMobile ? 200 : (width || 250)) : undefined;
  const fieldSx = { width: computedWidth, ...(sx || {}) };

  // color del ícono según tenga contenido
  const iconColor = value ? 'primary' : 'action.active';

  return (
    <Box sx={{ '& > :not(style)': { m: 1 } }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-end' }}>
        {Icon && <Icon sx={{ color: iconColor, mr: 1, my: 0.5 }} />}
        <NumericFormat
          value={value}
          onValueChange={handleChange}
          onBlur={handleBlur}
          customInput={TextField}
          thousandSeparator
          valueIsNumericString
          prefix={prefix}
          variant="standard"
          label={label}
          name={name}
          required={required}
          inputProps={{ maxLength, style: { textAlign: 'right' } }}
          allowNegative={allowNegative}
          isAllowed={isAllowed}
          sx={fieldSx}
        />
      </Box>
    </Box>
  );
}
